import { useState } from 'react';
import axios from 'axios';
import { useNavigate, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  LogOut, CreditCard, ShoppingBag, AlertCircle, User, Phone, Mail, MapPin,
  Calendar, Clock, CheckCircle2, XCircle, Save
} from 'lucide-react';
import { usePortal } from './PortalContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s) => s ? new Date(s).toLocaleDateString('es-ES') : '—';

const Tab = ({ id, active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={() => onClick(id)}
    data-testid={`portal-tab-${id}`}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm whitespace-nowrap transition ${
      active ? 'border-emerald-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
    }`}
  >
    <Icon size={16} />
    <span>{label}</span>
    {typeof count === 'number' && count > 0 && (
      <span className="ml-1 text-[10px] bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">{count}</span>
    )}
  </button>
);

const PortalDashboard = () => {
  const { member, loading, logout, refreshMember, authHeaders } = usePortal();
  const navigate = useNavigate();
  const [tab, setTab] = useState('resumen');
  const [profile, setProfile] = useState({
    email: '', address: '', emergency_contact: '', emergency_phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }
  if (!member) return <Navigate to="/" replace />;

  // Lazy-init profile form on first Perfil tab open
  const openPerfilTab = () => {
    setProfile({
      email: member.email || '',
      address: member.address || '',
      emergency_contact: member.emergency_contact || '',
      emergency_phone: member.emergency_phone || '',
    });
    setTab('perfil');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/portal/profile`, profile, { headers: authHeaders() });
      await refreshMember();
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingProfile(false);
    }
  };

  const activeSub = member.active_subscription;
  const daysLeft = activeSub?.subscription_end
    ? Math.ceil((new Date(activeSub.subscription_end).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 bg-[#0A0A0A] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL || ''}/fex-logo-light.png`}
              alt="FEX"
              className="h-8 w-auto select-none"
              draggable="false"
            />
            <div className="hidden sm:block">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight">
                <span className="text-white font-bold">FEX</span><span className="text-[#00FF9F] font-bold">.FIT</span>
              </p>
              <p className="text-[9px] uppercase tracking-widest text-zinc-600 leading-tight">Portal del Socio</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-zinc-500">{member.member_number}</p>
              <p className="text-sm font-medium">{member.name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="portal-logout">
              <LogOut size={14} className="mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Status banner */}
        <div className={`rounded-lg p-4 mb-6 border ${
          activeSub
            ? 'bg-emerald-900/15 border-emerald-800/50'
            : 'bg-amber-900/15 border-amber-800/50'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              {activeSub ? <CheckCircle2 className="text-emerald-400" size={22} /> : <XCircle className="text-amber-400" size={22} />}
              <div>
                <p className="text-sm font-medium">
                  {activeSub ? `Suscripción activa: ${activeSub.item_name}` : 'Sin suscripción activa'}
                </p>
                {activeSub && (
                  <p className="text-xs text-zinc-400">
                    Caduca el {formatDate(activeSub.subscription_end)}
                    {daysLeft !== null && daysLeft >= 0 && (
                      <span className={daysLeft <= 7 ? 'text-amber-400 ml-2' : 'text-zinc-500 ml-2'}>
                        · {daysLeft} día{daysLeft === 1 ? '' : 's'} restante{daysLeft === 1 ? '' : 's'}
                      </span>
                    )}
                  </p>
                )}
                {!activeSub && (
                  <p className="text-xs text-zinc-400">Acércate al gimnasio para renovar.</p>
                )}
              </div>
            </div>
            {member.pending_debt > 0 && (
              <Badge className="bg-red-600/20 text-red-300 border border-red-800/50">
                Deuda: {formatCurrency(member.pending_debt)}
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-900 mb-6 overflow-x-auto">
          <div className="flex">
            <Tab id="resumen" active={tab === 'resumen'} onClick={setTab} icon={CheckCircle2} label="Resumen" />
            <Tab id="suscripciones" active={tab === 'suscripciones'} onClick={setTab} icon={CreditCard} label="Suscripciones" count={member.subscriptions?.length} />
            <Tab id="compras" active={tab === 'compras'} onClick={setTab} icon={ShoppingBag} label="Compras" count={member.purchase_history?.length} />
            <Tab id="deudas" active={tab === 'deudas'} onClick={setTab} icon={AlertCircle} label="Deudas" count={member.debts?.length} />
            <Tab id="perfil" active={tab === 'perfil'} onClick={() => openPerfilTab()} icon={User} label="Perfil" />
          </div>
        </div>

        {/* Tab content */}
        {tab === 'resumen' && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="bg-[#0A0A0A] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <CreditCard className="text-emerald-400" size={20} />
                  <span className="text-xs text-zinc-500">Suscripción</span>
                </div>
                <p className="text-2xl font-bold mt-2">{activeSub?.item_name || '—'}</p>
                <p className="text-xs text-zinc-500">{activeSub ? `Hasta ${formatDate(activeSub.subscription_end)}` : 'Inactiva'}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <ShoppingBag className="text-blue-400" size={20} />
                  <span className="text-xs text-zinc-500">Compras</span>
                </div>
                <p className="text-2xl font-bold mt-2">{member.purchase_history?.length || 0}</p>
                <p className="text-xs text-zinc-500">Pedidos registrados</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <AlertCircle className={member.pending_debt > 0 ? 'text-red-400' : 'text-zinc-500'} size={20} />
                  <span className="text-xs text-zinc-500">Deuda</span>
                </div>
                <p className={`text-2xl font-bold mt-2 ${member.pending_debt > 0 ? 'text-red-400' : ''}`}>
                  {formatCurrency(member.pending_debt)}
                </p>
                <p className="text-xs text-zinc-500">{member.debts?.length || 0} pendiente{member.debts?.length === 1 ? '' : 's'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {tab === 'suscripciones' && (
          <div className="space-y-3" data-testid="portal-subs-list">
            {(!member.subscriptions || member.subscriptions.length === 0) ? (
              <p className="text-center text-zinc-500 py-8">No tienes suscripciones registradas.</p>
            ) : member.subscriptions.map((s, idx) => (
              <Card key={`${s.sale_id}-${s.item_index}`} className={`border ${s.is_active ? 'bg-emerald-900/10 border-emerald-800/50' : 'bg-[#0A0A0A] border-zinc-800'}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-lg">{s.item_name}</p>
                        {s.is_active
                          ? <Badge className="bg-emerald-600/20 text-emerald-400">Activa</Badge>
                          : s.refunded
                            ? <Badge className="bg-zinc-700/40 text-zinc-400">Devuelta</Badge>
                            : <Badge className="bg-amber-600/20 text-amber-400">Caducada</Badge>
                        }
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
                        <div className="text-zinc-500">Inicio: <span className="text-zinc-300">{formatDate(s.start_date)}</span></div>
                        <div className="text-zinc-500">Caduca: <span className={s.is_active ? 'text-emerald-300' : 'text-amber-300'}>{formatDate(s.subscription_end)}</span></div>
                        <div className="text-zinc-500">Duración: <span className="text-zinc-300">{s.duration_days} días</span></div>
                        <div className="text-zinc-500">Importe: <span className="text-zinc-300 font-mono">{formatCurrency(s.total_final)}</span></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'compras' && (
          <div className="space-y-2" data-testid="portal-purchases-list">
            {(!member.purchase_history || member.purchase_history.length === 0) ? (
              <p className="text-center text-zinc-500 py-8">No tienes compras registradas.</p>
            ) : member.purchase_history.map((p) => (
              <Card key={p.sale_id} className="bg-[#0A0A0A] border-zinc-800">
                <CardContent className="p-3 flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      {(p.items || []).map(i => `${i.item_name} x${i.quantity}`).join(', ')}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                      <Clock size={11} /> {formatDate(p.created_at)} · {p.payment_method}
                      {p.refunded && <span className="text-red-400">· Devuelta</span>}
                    </p>
                  </div>
                  <span className={`font-mono text-sm shrink-0 ${p.refunded ? 'text-zinc-500 line-through' : 'text-emerald-400'}`}>
                    {formatCurrency(p.total_final)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'deudas' && (
          <div className="space-y-2" data-testid="portal-debts-list">
            {(!member.debts || member.debts.length === 0) ? (
              <p className="text-center text-zinc-500 py-8">¡No tienes deudas pendientes! 🎉</p>
            ) : member.debts.map((d) => (
              <Card key={d.id} className="bg-red-900/10 border-red-800/50">
                <CardContent className="p-3 flex justify-between items-center gap-2">
                  <div>
                    <p className="text-sm">{d.description || 'Deuda'}</p>
                    <p className="text-xs text-zinc-500">
                      <Calendar size={11} className="inline mr-1" />
                      {formatDate(d.created_at)}
                      {d.status === 'partial' && <span className="ml-2 text-amber-400">Pago parcial</span>}
                    </p>
                  </div>
                  <span className="font-mono text-red-400 font-semibold">{formatCurrency(d.remaining_amount)}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'perfil' && (
          <div className="space-y-4" data-testid="portal-profile-tab">
            <Card className="bg-[#0A0A0A] border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><User size={16} /> Tus datos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-500">Nº de socio</Label>
                    <Input value={member.member_number || '—'} disabled className="bg-zinc-900 border-zinc-700 font-mono mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Nombre</Label>
                    <Input value={member.name || ''} disabled className="bg-zinc-900 border-zinc-700 mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-500"><Phone size={11} className="inline mr-1" /> Teléfono</Label>
                    <Input value={member.phone || ''} disabled className="bg-zinc-900 border-zinc-700 mt-1" />
                    <p className="text-[10px] text-zinc-600 mt-1">Contacta al gimnasio para cambiarlo.</p>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500"><Mail size={11} className="inline mr-1" /> Email</Label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 mt-1"
                      data-testid="portal-profile-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500"><MapPin size={11} className="inline mr-1" /> Dirección</Label>
                  <Input
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    placeholder="Calle, número, ciudad…"
                    className="bg-zinc-900 border-zinc-700 mt-1"
                    data-testid="portal-profile-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-500">Contacto de emergencia</Label>
                    <Input
                      value={profile.emergency_contact}
                      onChange={(e) => setProfile({ ...profile, emergency_contact: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 mt-1"
                      data-testid="portal-profile-emergency-contact"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Teléfono de emergencia</Label>
                    <Input
                      value={profile.emergency_phone}
                      onChange={(e) => setProfile({ ...profile, emergency_phone: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 mt-1"
                      data-testid="portal-profile-emergency-phone"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-profile-save">
                    <Save size={14} className="mr-2" />
                    {savingProfile ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalDashboard;
